import React, { useId } from "react"

import { ErrorMessage } from "@hookform/error-message"
import ChevronUp from "@mui/icons-material/ExpandLessOutlined"
import ChevronDown from "@mui/icons-material/ExpandMoreOutlined"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import FormHelperText from "@mui/material/FormHelperText"
import InputAdornment from "@mui/material/InputAdornment"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { SxProps, Theme, styled } from "@mui/material/styles"
import { Controller, FieldValue, useWatch } from "react-hook-form"
import {
  NumericFormat,
  NumericFormatProps,
  NumberFormatValues,
} from "react-number-format"

import { getCurrencySymbol } from "@components/preloadedSelects/CurrencyAutoComplete"

import { MAX_FRACTION_DIGITS_FOR_QUANTITIES } from "@constants/globalStrings"

import Label from "../Label"

const ChevronIconsStyle = {
  border: "none",
  background: "none",
  padding: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "fit-content",
  paddingRight: "8px",
}

const ChevronIconsWrapperStyle = {
  display: "flex",
  flexDirection: "column",
  marginLeft: "4px",
  marginRight: "4px",
  "& svg": {
    width: "16px",
    height: "16px",
    display: "block",
    strokeWidth: 2.5,
  },
}

interface CustomProps {
  currencyCode: string
  fixedDecimalScale?: boolean
  isCurrency: boolean
  name: string
  numDecimalPlaces?: number
  onChange: (event: { target: { name: string; value: string } }) => void
  onIncrement?: () => void
  onDecrement?: () => void
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const {
      currencyCode,
      fixedDecimalScale,
      isCurrency,
      numDecimalPlaces,
      onChange,
      onIncrement,
      onDecrement,
      ...other
    } = props

    const currencySymbol = isCurrency ? getCurrencySymbol(currencyCode) : ""
    const [isFocused, setIsFocused] = React.useState(false)

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "ArrowUp") {
        e.preventDefault()
        onIncrement?.()
      } else if (e.key === "ArrowDown") {
        e.preventDefault()
        onDecrement?.()
      }
    }

    return (
      <NumericFormat
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...other}
        getInputRef={ref}
        onValueChange={(values: NumberFormatValues) => {
          if (isFocused) {
            onChange({
              target: {
                name: props.name,
                value: values.value,
              },
            })
          }
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        thousandSeparator
        valueIsNumericString
        decimalScale={
          numDecimalPlaces ??
          (isCurrency ? 2 : MAX_FRACTION_DIGITS_FOR_QUANTITIES)
        }
        fixedDecimalScale={!!fixedDecimalScale}
        prefix={`${currencySymbol} `}
      />
    )
  },
)

export const InputNumericFieldContainerDiv = styled("div")({
  display: "flex",
  flex: 1,
  flexDirection: "column",
})

interface InputNumericFieldProps {
  control: any // eslint-disable-line @typescript-eslint/no-explicit-any
  errors: any // eslint-disable-line @typescript-eslint/no-explicit-any
  name: string

  // NOT REQUIRED
  additionalLowerHorizontalLabel?: string
  additionalUpperHorizontalLabel?: string
  chip?: string
  currencyCode?: string
  dataCy?: string
  defaultValue?: string
  disabled?: boolean
  endAdornment?: string
  fixedDecimalScale?: boolean
  helperText?: string
  horizontal?: boolean
  isCurrency?: boolean
  label?: string
  loading?: boolean
  multiline?: boolean
  numDecimalPlaces?: number
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onIncrement?: () => void
  onDecrement?: () => void
  placeholder?: string
  register?: any // eslint-disable-line @typescript-eslint/no-explicit-any
  removeBorder?: boolean
  required?: boolean
  rows?: number
  showIncrementButtons?: boolean
  startAdornment?: string
  sx?: any // eslint-disable-line @typescript-eslint/no-explicit-any
  sxInput?: SxProps<Theme>
  sxLabel?: any // eslint-disable-line @typescript-eslint/no-explicit-any
  sxLabelWrapper?: any // eslint-disable-line @typescript-eslint/no-explicit-any
  sxWrapper?: any // eslint-disable-line @typescript-eslint/no-explicit-any
  type?: string
}

const InputNumericField: React.FC<InputNumericFieldProps> = ({
  additionalLowerHorizontalLabel,
  additionalUpperHorizontalLabel,
  chip,
  control,
  currencyCode,
  dataCy,
  defaultValue = "",
  disabled,
  endAdornment,
  errors = [],
  fixedDecimalScale,
  helperText,
  horizontal,
  isCurrency,
  label,
  loading,
  multiline,
  name,
  numDecimalPlaces,
  onChange,
  onKeyDown = () => {},
  onIncrement,
  onDecrement,
  placeholder,
  register = () => {},
  removeBorder,
  required,
  rows,
  showIncrementButtons = false,
  startAdornment,
  sx,
  sxInput,
  sxLabel,
  sxLabelWrapper,
  sxWrapper,
  type,
}: InputNumericFieldProps) => {
  const id = useId()
  const watchedValue = useWatch({
    control,
    name,
    defaultValue,
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleIncrement = (field: FieldValue<any>) => {
    const currentValue = parseFloat(field.value || "1")
    if (currentValue < 500) {
      if (onIncrement) {
        onIncrement()
      } else {
        field.onChange(Math.min(currentValue + 1, 500).toString())
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDecrement = (field: FieldValue<any>) => {
    const currentValue = parseFloat(field.value || "1")
    if (currentValue > 1) {
      if (onDecrement) {
        onDecrement()
      } else {
        field.onChange(Math.max(currentValue - 1, 1).toString())
      }
    }
  }

  const wrapperStyling = horizontal
    ? {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        ...sxWrapper,
      }
    : sxWrapper
  const labelStyling = horizontal
    ? {
        mb: 0,
        ...sxLabel,
      }
    : sxLabel
  const inputStyling = horizontal
    ? {
        ml: "auto",
        flex: 1,
        "& input": {
          textAlign: "right",
        },
        ...sx,
      }
    : sx

  const startInputAdornment = (currencyCode || startAdornment) && (
    <InputAdornment position="start">
      {startAdornment && (
        <Typography variant="body1">{startAdornment}</Typography>
      )}
    </InputAdornment>
  )

  let endInputAdornment = endAdornment && (
    <InputAdornment position="end" sx={{ textTransform: "lowercase" }}>
      {endAdornment && <Typography variant="body1">{endAdornment}</Typography>}
    </InputAdornment>
  )

  if (loading) {
    return (
      <InputNumericFieldContainerDiv style={wrapperStyling}>
        {(label || helperText) && (
          <Stack
            sx={
              horizontal
                ? { width: "50%", pr: 4, ...sxLabelWrapper }
                : sxLabelWrapper
            }
          >
            {label && (
              <Label required={required} sx={labelStyling} htmlFor={id}>
                {label}{" "}
                {chip && (
                  <Chip
                    label={chip}
                    size="small"
                    sx={{ fontSize: "10px", height: "20px", ml: 1.5 }}
                  />
                )}
              </Label>
            )}
            {helperText && (
              <FormHelperText sx={{ mb: 0.5 }}>{helperText}</FormHelperText>
            )}
          </Stack>
        )}
        <Stack>
          {additionalUpperHorizontalLabel && horizontal && (
            <Typography variant="caption">
              {additionalUpperHorizontalLabel}
            </Typography>
          )}
          <TextField
            data-testid="skeleton-input"
            disabled
            id={id}
            InputProps={{
              endAdornment: endInputAdornment,
              startAdornment: startInputAdornment,
              sx: removeBorder
                ? {
                    p: 0,
                    "& fieldset": {
                      border: "none",
                      p: 0,
                    },
                  }
                : {},
            }}
            size="small"
            sx={{ width: "100%", ...inputStyling }}
            type={type}
            variant="outlined"
          />
        </Stack>
      </InputNumericFieldContainerDiv>
    )
  }

  return (
    <InputNumericFieldContainerDiv style={wrapperStyling}>
      {(label || helperText) && (
        <Stack
          sx={
            horizontal
              ? { width: "50%", pr: 4, ...sxLabelWrapper }
              : sxLabelWrapper
          }
        >
          {label && (
            <Label required={required} sx={labelStyling} htmlFor={id}>
              {label}{" "}
              {chip && (
                <Chip
                  label={chip}
                  size="small"
                  sx={{ fontSize: "10px", height: "20px", ml: 1.5 }}
                />
              )}
            </Label>
          )}
          {helperText && (
            <FormHelperText sx={{ mb: 0.5 }}>{helperText}</FormHelperText>
          )}
        </Stack>
      )}
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        key={name}
        data-cy={dataCy}
        render={({ field }) => {
          if (showIncrementButtons) {
            endInputAdornment = (
              <Stack sx={ChevronIconsWrapperStyle}>
                <Button
                  type="button"
                  onClick={() => handleIncrement(field)}
                  disabled={disabled}
                  sx={ChevronIconsStyle}
                >
                  <ChevronUp />
                </Button>
                <Button
                  type="button"
                  onClick={() => handleDecrement(field)}
                  disabled={disabled}
                  sx={ChevronIconsStyle}
                >
                  <ChevronDown />
                </Button>
              </Stack>
            )
          } else if (endAdornment) {
            endInputAdornment = (
              <InputAdornment position="end">
                <Typography variant="body1">{endAdornment}</Typography>
              </InputAdornment>
            )
          }

          return (
            <Stack>
              {additionalUpperHorizontalLabel && horizontal && (
                <Typography variant="caption" sx={{ ml: "auto", mb: 0.5 }}>
                  {additionalUpperHorizontalLabel}
                </Typography>
              )}
              <TextField
                aria-label={name}
                id={id}
                data-testid="currencyfield-input"
                disabled={disabled}
                data-cy={dataCy}
                error={!!errors[name]}
                InputProps={{
                  inputProps: {
                    currencyCode,
                    isCurrency,
                    onChange,
                    numDecimalPlaces,
                    fixedDecimalScale,
                    onIncrement: () => handleIncrement(field),
                    onDecrement: () => handleDecrement(field),
                    style: sxInput ?? {},
                  },
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  inputComponent: NumericFormatCustom as any,
                  endAdornment: endInputAdornment,
                  startAdornment: startInputAdornment,
                  sx: removeBorder
                    ? {
                        p: 0,
                        "& fieldset": {
                          border: "none",
                          p: 0,
                        },
                      }
                    : {},
                }}
                multiline={multiline}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                rows={rows}
                size="small"
                sx={{ width: "100%", ...inputStyling }}
                type={type}
                variant="outlined"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register(name)}
                value={watchedValue || field.value}
              />
              {additionalLowerHorizontalLabel && horizontal && (
                <Typography variant="caption" sx={{ ml: "auto", mt: 1 }}>
                  {additionalLowerHorizontalLabel}
                </Typography>
              )}
              <ErrorMessage
                errors={errors}
                name={name}
                render={({ message }) => (
                  <FormHelperText
                    error
                    sx={{ textAlign: horizontal ? "right" : "left" }}
                  >
                    {message}
                  </FormHelperText>
                )}
              />
            </Stack>
          )
        }}
      />
    </InputNumericFieldContainerDiv>
  )
}

export default InputNumericField
